import React, { useState } from "react";
import { Link } from "react-router-dom";
import { User, LogOut, Ticket as TicketIcon, ChevronDown, Loader2 } from "lucide-react";

import { useUserAuth } from "../../context/UserAuthContext";

/** Small auth widget rendered in the top-right corner of the landing page.
 *  Shows "Entrar / Criar conta" when logged out, and a user dropdown (with
 *  link to /conta and logout) when logged in. */
export const AccountHeaderWidget = ({ accentColor = "#f59e0b" }) => {
  const { user, loading, logout } = useUserAuth();
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-30">
        <div className="px-3 py-2 rounded-full bg-black/40 backdrop-blur border border-white/10">
          <Loader2 className="w-4 h-4 animate-spin text-white/60" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-30 flex items-center gap-2" data-testid="account-widget-anon">
        <Link
          to="/login"
          className="text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full bg-black/40 backdrop-blur border border-white/15 text-white hover:bg-black/60 transition-colors"
          data-testid="header-login-btn"
        >
          Entrar
        </Link>
        <Link
          to="/registar"
          className="text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-full font-medium text-black hover:opacity-90 transition-opacity"
          style={{ backgroundColor: accentColor }}
          data-testid="header-register-btn"
        >
          Criar conta
        </Link>
      </div>
    );
  }

  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;

  return (
    <div className="fixed top-4 right-4 sm:top-6 sm:right-6 z-30" data-testid="account-widget-auth">
      <div className="relative">
        <button
          onClick={() => setOpen((v) => !v)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          className="flex items-center gap-2 px-3 py-2 rounded-full bg-black/40 backdrop-blur border border-white/15 text-white hover:bg-black/60 transition-colors"
          data-testid="header-account-btn"
        >
          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-black" style={{ backgroundColor: accentColor }}>
            {(user.first_name?.[0] || user.email[0] || "?").toUpperCase()}
          </div>
          <span className="text-sm font-medium hidden sm:inline max-w-[160px] truncate">{name}</span>
          <ChevronDown className="w-3.5 h-3.5 text-white/60 hidden sm:block" />
        </button>

        {open && (
          <div
            className="absolute right-0 mt-2 w-60 bg-zinc-950 border border-white/10 rounded-xl overflow-hidden shadow-2xl"
            data-testid="header-account-dropdown"
            onMouseDown={(e) => e.preventDefault()}
          >
            <div className="px-4 py-3 border-b border-white/5">
              <p className="text-sm font-semibold text-white truncate">{name}</p>
              <p className="text-xs text-zinc-500 truncate">{user.email}</p>
            </div>
            <Link
              to="/conta"
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-zinc-200 hover:bg-white/5"
              data-testid="header-account-link"
            >
              <TicketIcon className="w-4 h-4 text-zinc-400" /> A Minha Conta
            </Link>
            <button
              onClick={async () => { await logout(); setOpen(false); }}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-400 hover:bg-white/5 border-t border-white/5"
              data-testid="header-account-logout"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
